import React, { useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAppStore } from '../../stores/useAppStore';
import { setTelemetryUser } from '../../utils/errorReporting';
import { useMsal } from '@azure/msal-react';

interface Props {
  children: React.ReactNode;
}

export const AuthBootstrap: React.FC<Props> = ({ children }) => {
  const { isReady, getApiAccessToken } = useAuth();
  const tokenProviderSetRef = useRef(false);
  const bootstrapStartTimeRef = useRef<number>(performance.now());
  const tokenProvider = useCallback(async (): Promise<string | null> => {
    const apiToken = await getApiAccessToken();
    if (!apiToken && import.meta.env.DEV) {
      console.error('❌ AuthBootstrap: API токен не получен. Проверьте настройки Azure AD и scope токена.');
    }
    return apiToken;
  }, [getApiAccessToken]);

  useLayoutEffect(() => {
    useAppStore.getState().setAccessTokenProvider(tokenProvider);
    tokenProviderSetRef.current = true;
  }, [tokenProvider]);

  useEffect(() => {
    if (isReady && import.meta.env.DEV) {
      const bootstrapTime = performance.now() - bootstrapStartTimeRef.current;
      console.log(`🚀 AuthBootstrap: Готов за ${bootstrapTime.toFixed(0)}ms`);
    }
  }, [isReady]);

  const { instance } = useMsal();

  // Если active account ещё не проставлен (например, после перезагрузки страницы
  // с несколькими аккаунтами в кэше MSAL), выставляем его один раз, чтобы весь
  // код (useAuth, getAccessToken, getApiAccessToken) работал с одним и тем же
  // пользователем. Без этого useAccount() без аргументов вернёт null, а
  // сторонние места вроде SupportTicketModal могут получить чужую личность.
  useEffect(() => {
    if (instance.getActiveAccount()) return;
    const firstAccount = instance.getAllAccounts()[0];
    if (firstAccount) {
      instance.setActiveAccount(firstAccount);
    }
  }, [instance]);

  // Устанавливаем контекст пользователя для App Insights
  useEffect(() => {
    if (!isReady) return;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return;
    const userId = account.localAccountId || account.homeAccountId;
    setTelemetryUser(userId, account.username);
  }, [isReady, instance]);

  return <>{children}</>;
};

export default AuthBootstrap;



